<template>
  <Layout
    :errors="errors"
    :title="'Dashboard: ' + trans(`models.${resourceType}.name.plural`)"
    :toasts="toasts"
    :resource="model"
  >
    <template #top>
      <resource-action-header
        :model="model"
        :action="'dashboard'"
        :title="trans(`models.${resourceType}.name.plural`)"
      ></resource-action-header>
    </template>
    <ul class="nav nav-tabs gap-1" style="margin: 0 0 -1px 0.5rem">
      <li class="nav-item">
        <button :class="focus === 'self' ? 'active' : 'text-muted'" class="nav-link" @click="setFocus('self')">
          {{ trans(`models.${resourceType}.name.singular`) }}
        </button>
      </li>
      <template v-if="model.data.id">
        <template v-for="(collection, name) in model.data.collections" :key="name">
          <li v-if="meta.editable.includes(name)" class="nav-item">
            <button :class="focus === name ? 'active' : 'text-muted'" class="nav-link" @click="setFocus(name)">
              {{ trans(`models.${resourceType}.relations.${name}`) }}
            </button>
          </li>
        </template>
      </template>
    </ul>
    <div v-if="model.data && focus === 'self'" class="card rounded-corner">
      <div class="card-body position-relative">
        <div class="model-title-bar">
          <h5 class="card-title text-primary fw-bold text-uppercase">
            {{ trans(`models.${resourceType}.content.` + (model.data.id ? 'edit' : 'create') + `.action`) }}
          </h5>
          <p class="card-text">
            {{ trans(`models.${resourceType}.content.` + (model.data.id ? 'edit' : 'create') + `.description`) }}
          </p>
        </div>
        <resource-form :key="model.data.id" :errors="errors" :resource="model"></resource-form>
      </div>
    </div>
    <div v-if="model.data.id">
      <div v-for="(collection, name) in model.data.collections" :key="name">
        <template v-if="meta.editable.includes(name) && focus == name">
          <relation-collection-card
            :name="name"
            :resource="model"
            @updatePicked="(picked) => updatePicked(picked, name)"
            @detach="(detached) => detach(name, detached)"
            @trigger:refresh="reload()"
          ></relation-collection-card>
          <div v-if="model.meta.belongsToMany[name]" class="row">
            <div class="col-12">
              <card
                :id="`resource-component_${resourceType}-${name}_selection`"
                :title="trans('models.' + resourceType + '.relations.' + name) + ' hinzufügen'"
                class="mb-2"
              >
                <resource-selection
                  :name="name"
                  :resource-type="model.meta.collections[name]"
                  :origin="model"
                  @update:attached="(attached) => attach(name, attached)"
                ></resource-selection>
              </card>
            </div>
          </div>
          <div v-else-if="model.meta.hasMany[name]" class="row">
            <div class="col-12">
              <picked-resource-card :name="name" :picked="picked[name]" :resource="model"></picked-resource-card>
            </div>
          </div>
        </template>
      </div>
    </div>
    <template #footer-main>
      <div v-if="models.page" class="p-3">
        <pagination :name="trans(`models.${resourceType}.name.plural`)" :resource="models"></pagination>
      </div>
    </template>
    <template #right>
      <admin-sidebox :resource="model" :resource-collection="models" :focus="'resources'"></admin-sidebox>
    </template>
  </Layout>
</template>

<script>
import AdminSidebox from '@kanrisha/components/Layout/AdminSidebox';
import Card from '@kanrisha/components/Base/Card';
import Layout from '@kanrisha/Pages/Layout';
import ResourceForm from '@kanrisha/components/ResourceForm';
import Pagination from '@kanrisha/components/Pagination';
import ResourceActionButtons from '@kanrisha/components/Links/ResourceActionButtons';
import ResourceCollection from '@kanrisha/components/ResourceCollection';
import ResourceSelection from '@kanrisha/components/ResourceSelection';
import useCustomActionBox from '@kanrisha/composables/useCustomActionBox';
import useResourceParams from '@kanrisha/composables/useResourceParams';
import useToasts from '@kanrisha/composables/useToasts';
import useResource from '@kanrisha/composables/useResource';
import useModels from '@kanrisha/composables/useModels';
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import PickedResourceCard from '@kanrisha/components/Cards/PickedResource';
import RelationCollectionCard from '@kanrisha/components/Cards/RelationCollection';
import ResourceActionHeader from '@kanrisha/components/Layout/ResourceActionHeader';

export default {
  components: {
    ResourceActionHeader,
    AdminSidebox,
    Card,
    Layout,
    ResourceForm,
    Pagination,
    PickedResourceCard,
    RelationCollectionCard,
    ResourceActionButtons,
    ResourceCollection,
    ResourceSelection,
  },
  props: {
    aspectMenuItems: { type: Object, default: () => {} },
    model: { type: Object, default: () => {} },
    models: { type: Object, default: () => {} },
    toasts: { type: Object, default: () => {} },
    errors: { type: Object, default: () => {} },
    config: { type: Object, default: () => {} },
    customActionData: { type: Object, default: () => {} },
  },
  setup(props) {
    const { meta, resourceType } = useResource(props, props.model);
    const picked = ref(props.model.data.picked);
    const { customActionBox } = useCustomActionBox(props);
    const { addToast } = useToasts(props);
    const { getIndexParams } = useResourceParams(props);
    const focus = ref(props.model.meta.adjustments.focus ?? 'self');

    const hasDescription = (trans) => {
      let transString = `models.${props.resourceType}.content.index.description`;
      let translation = trans(transString);

      return translation && translation !== transString;
    };

    const updatePicked = (pickedModel, name) => {
      picked.value[name].data = pickedModel.model.data;
    };

    const setFocus = (name) => {
      const { focus: setFocusAdjustment } = useModels({ resourceType: props.model.meta.resourceType });
      setFocusAdjustment(props.model.meta.resourceType, name);
      focus.value = name;
    };

    const attach = async (name, attached) => {
      const { attachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await attachModel(props.model.data.id, props.model.meta.resourceType, name, attached.data.id);
      router.reload();
    };

    const detach = async (name, detached) => {
      const { detachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await detachModel(props.model.data.id, props.model.meta.resourceType, name, detached.data.id);
      router.reload();
    };

    const reload = () => {
      router.reload();
    };

    return {
      addToast,
      attach,
      customActionBox,
      detach,
      focus,
      getIndexParams,
      hasDescription,
      meta,
      picked,
      reload,
      resourceType,
      setFocus,
      updatePicked,
    };
  },
};
</script>
